/** React */
import { useState } from "react";

/** Images */
import ContactImg from "../../assets/chatContact.png";

/** MUI */
import {
    Box,
    Container,
    Grid2 as Grid,
    MenuItem,
    Typography,
} from "@mui/material";

/** Services */
import contactServ from "../../services/contactServ";

/** Components */
import BaseBackdrop from "../ui/BaseBackdrop";
import BaseButton from "../ui/BaseButton";
import BaseSelect from "../ui/BaseSelect";
import BaseSnackbar from "../ui/BaseSnackbar";
import BaseTextField from "../ui/BaseTextField";
import classes from "./contact.module.css";

const defaultData = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    system: "",
    priority: "",
};

const Contact = (props) => {
    const { sectionRef } = props;

    /** Component states */
    const [formData, setFormData] = useState(defaultData);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [show, setShow] = useState(false);
    const [texto, setTexto] = useState("");

    /** Component variables */
    const priorities = ["Save time & money", "Attract & retain top talent", "Deliver exceptional services"];
    const systems = ["Xero", "Quickbooks online", "Other"];

    /** Component functions */
    const handleChangeData = (event) => {
        setFormData({
            ...formData,
            [event.target.name]: event.target.value,
        });
    };

    const sendInfo = async () => {
        if (
            !formData.firstName.trim() ||
            !formData.lastName.trim() ||
            !formData.email.trim() ||
            !formData.phone.trim() ||
            formData.system === "" ||
            formData.priority === ""
        ) {
            setIsError(true);
        } else {
            setIsError(false);

            const finalData = {
                first_name: formData.firstName,
                last_name: formData.lastName,
                system_integration: formData.system + 1,
                customer_email: formData.email,
                practice_priority: formData.priority + 1,
                phone_number: formData.phone,
            };
            setIsLoading(true);

            await contactServ
                .sendContactEmail(finalData)
                .then(() => {
                    setTexto(
                        <Box className={classes.backColor}>
                            <Typography color="V080" variant="h2" className={classes.paddingBSnack}>
                                Thank you for your interest!
                            </Typography>
                            <Typography color="V080">
                                We've sent you an email with more information. Please check your inbox (including
                                spam/junk).
                            </Typography>
                            <Typography color="V080" className={classes.paddingBSmallSnack}>
                                An Account Executive will be in touch shortly (EST working time) to explore how Noddok
                                can streamline your practice's processes.
                            </Typography>
                            <Typography color="V080">The Noddok team</Typography>
                        </Box>
                    );
                    setShow(true);
                    setFormData(defaultData);
                })
                .catch((err) => {
                    console.log(err);
                    setTexto("Error");
                    setShow(true);
                });

            setIsLoading(false);
        }
    };

    const handleCloseSnackBar = (event, reason) => {
        if (reason === "clickaway") return;
        setShow(false);
    };

    return (
        <Box className={classes.contact} ref={sectionRef}>
            <img src={ContactImg} alt="Contact" className={classes.contactImg} />
            <Container maxWidth="xl" disableGutters className={classes.contactSpacing}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 3.6, lg: 3.8, xxl: 5 }}></Grid>
                    <Grid size={{ xs: 12, md: 8.4, lg: 8.2, xxl: 7 }}>
                        <Typography variant="h1" textAlign="center" color="secondary" className={classes.paddingB}>
                            Let's chat about your practice's specific needs
                        </Typography>
                        <Box className={classes.form}>
                            <Grid container spacing={{ xs: 2, xxl: 3 }} justifyContent="flex-end">
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <BaseTextField
                                        name="firstName"
                                        label="First name"
                                        value={formData.firstName}
                                        onChange={handleChangeData}
                                        error={isError && !formData.firstName.trim()}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <BaseTextField
                                        name="lastName"
                                        label="Last name"
                                        value={formData.lastName}
                                        onChange={handleChangeData}
                                        error={isError && !formData.lastName.trim()}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <BaseTextField
                                        name="email"
                                        label="Work e-mail"
                                        value={formData.email}
                                        onChange={handleChangeData}
                                        error={isError && !formData.email.trim()}
                                        type="email"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <BaseTextField
                                        name="phone"
                                        label="Phone number"
                                        value={formData.phone}
                                        onChange={handleChangeData}
                                        error={isError && !formData.phone.trim()}
                                        fullWidth
                                        type="tel"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <BaseSelect
                                        name="system"
                                        label="What's your preferred system?"
                                        value={formData.system}
                                        onChange={handleChangeData}
                                        error={isError && formData.system === ""}
                                    >
                                        {systems.map((item, index) => (
                                            <MenuItem value={index} key={index}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </BaseSelect>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <BaseSelect
                                        name="priority"
                                        label="What's your practice top priority?"
                                        value={formData.priority}
                                        onChange={handleChangeData}
                                        error={isError && formData.priority === ""}
                                    >
                                        {priorities.map((item, index) => (
                                            <MenuItem value={index} key={index}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </BaseSelect>
                                </Grid>
                                <Grid sx={{ paddingTop: 1 }}>
                                    <BaseButton
                                        onClick={sendInfo}
                                        variantType="secondaryContainedButton"
                                        sizebutton="l"
                                        disableElevation
                                    >
                                        Talk to an Executive
                                    </BaseButton>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
            <BaseSnackbar
                show={show}
                message={texto}
                padding={{ xs: "24px 32px", md: "32px 48px" }}
                backgroundColor="#D8F5EE"
                onClose={handleCloseSnackBar}
            />
            <BaseBackdrop open={isLoading} />
        </Box>
    );
};

export default Contact;